/* global window ResizeObserver */
import * as React from "react";
import PropTypes from "prop-types";
import { useSelector, shallowEqual } from "react-redux";
import { getPinsForMoving } from "../../helpers/grid";

const CellShapeElement = (props) => {
  const { groupId, x, y } = props;
  const id = `${props.type}_${x}_${y}`;

  const shapeRef = React.useRef();
  const [innerSize, setInnerSize] = React.useState({ width: window.innerWidth, height: window.innerHeight });
  React.useEffect(() => {
    const updateInnerSize = () => {
      setInnerSize({ width: window.innerWidth, height: window.innerHeight });
    };

    updateInnerSize();

    if (typeof ResizeObserver !== "undefined" && shapeRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        updateInnerSize();
      });
      resizeObserver.observe(shapeRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }

    return undefined;
  }, []);

  // which cell I am in the group? shallowEqual keeps the returned array
  // referentially stable across unrelated dispatches (hover, selection, autofit),
  // so this component only re-renders when the group membership actually changes.
  const otherCellIds = useSelector(
    (state) =>
      Object.keys(state.gridd.present.cellStyles).filter(
        (k) => state.gridd.present.cellStyles[k].shapeGroupId === groupId
      ),
    shallowEqual
  );
  const cells = useSelector((state) => state.gridd.present.structure.cells);
  const cols = useSelector((state) => state.gridd.present.structure.columns);
  const rows = useSelector((state) => state.gridd.present.structure.rows);

  // The coordinate scan + trig/path math is the expensive part. Memoize it so it
  // only recomputes when its real inputs change, not on every parent render.
  const { width, height, path } = React.useMemo(() => {
    const idSet = new Set(otherCellIds);
    const otherCellCoords = cells
      .flatMap((row, ri) => row.map((cell, ci) => (idSet.has(cell.id) ? { y: ci, x: ri, id: cell.id } : null)))
      .filter((c) => c !== null);

    const myIndex = otherCellCoords.findIndex((c) => c.x === x && c.y === y);
    const myLevel = props.reverse ? otherCellCoords.length - myIndex - 1 : myIndex;
    const horizontal = otherCellCoords.every((c) => c.x === x);

    // what is my cell's dimensions?
    const cpins = getPinsForMoving(cols, innerSize.width);
    const rpins = getPinsForMoving(rows, innerSize.height);
    const width = cpins[y + 1] - cpins[y];
    const height = rpins[x + 1] - rpins[x];

    // what are the shape's points?
    const gap = props.gap ?? 4;
    const margin = props.margin ?? 4;
    const reverse = props.reverse;
    const baseCellCoord = reverse ? otherCellCoords[0] : otherCellCoords[otherCellCoords.length - 1];
    let totalHeight, shapeTotalHeight, shapeBaseWidth, sideLength, alpha, gamma;
    let path;
    let d, topHeight, dt, bottomHeight, db;
    if (horizontal) {
      totalHeight = otherCellCoords.map((c) => cpins[c.y + 1] - cpins[c.y]).reduce((a, n) => (a += n), 0);
      shapeTotalHeight = totalHeight - 2 * margin;
      shapeBaseWidth = rpins[baseCellCoord.x + 1] - rpins[baseCellCoord.x] - 2 * margin;
      sideLength = Math.sqrt(
        (shapeBaseWidth / 2) * (shapeBaseWidth / 2) +
          shapeTotalHeight * shapeTotalHeight -
          2 * shapeBaseWidth * shapeTotalHeight * Math.cos(Math.PI / 2)
      );
      alpha = Math.asin(shapeTotalHeight / sideLength);
      gamma = Math.PI / 2 - alpha;

      if (myLevel === 0) {
        d = ((width - gap) * Math.sin(gamma)) / Math.sin(alpha);
        path = `M ${margin} ${height / 2} L ${width - gap} ${height / 2 - d} V ${height / 2 + d} Z`;
      } else {
        topHeight =
          otherCellCoords
            .slice(0, myLevel)
            .map((c) => cpins[c.y + 1] - cpins[c.y])
            .reduce((a, n) => (a += n), 0) + gap;
        dt = (topHeight * Math.sin(gamma)) / Math.sin(alpha);
        const bg = myLevel === otherCellCoords.length - 1 ? margin : gap;
        bottomHeight =
          otherCellCoords
            .slice(0, myLevel + 1)
            .map((c) => cpins[c.y + 1] - cpins[c.y])
            .reduce((a, n) => (a += n), 0) - bg;
        db = (bottomHeight * Math.sin(gamma)) / Math.sin(alpha);

        path = `M ${gap} ${height / 2 - dt} L ${gap} ${height / 2 + dt} L ${width - bg} ${height / 2 + db} L ${width - bg} ${height / 2 - db} Z`;
      }
    } else {
      totalHeight = otherCellCoords.map((c) => rpins[c.x + 1] - rpins[c.x]).reduce((a, n) => (a += n), 0);
      shapeTotalHeight = totalHeight;
      shapeBaseWidth = cpins[baseCellCoord.y + 1] - cpins[baseCellCoord.y] - 2 * margin;
      sideLength = Math.sqrt(
        (shapeBaseWidth / 2) * (shapeBaseWidth / 2) +
          shapeTotalHeight * shapeTotalHeight -
          2 * shapeBaseWidth * shapeTotalHeight * Math.cos(Math.PI / 2)
      );
      alpha = Math.asin(shapeTotalHeight / sideLength);
      gamma = Math.PI / 2 - alpha;

      if (myLevel === 0) {
        // render triangle
        d = ((height - gap) * Math.sin(gamma)) / Math.sin(alpha);
        path = `M ${width / 2} ${margin} L ${width / 2 - d} ${height - gap} H ${width / 2 + d} Z`;
      } else {
        topHeight =
          otherCellCoords
            .slice(0, myLevel)
            .map((c) => rpins[c.x + 1] - rpins[c.x])
            .reduce((a, n) => (a += n), 0) + gap;
        dt = (topHeight * Math.sin(gamma)) / Math.sin(alpha);
        const bg = myLevel === otherCellCoords.length - 1 ? margin : gap;
        bottomHeight =
          otherCellCoords
            .slice(0, myLevel + 1)
            .map((c) => rpins[c.x + 1] - rpins[c.x])
            .reduce((a, n) => (a += n), 0) - bg;
        db = (bottomHeight * Math.sin(gamma)) / Math.sin(alpha);

        path = `M ${width / 2 - dt} ${gap} L ${width / 2 + dt} ${gap} L ${width / 2 + db} ${height - bg} L ${width / 2 - db} ${height - bg} Z`;
      }
    }

    return { width, height, path };
  }, [
    otherCellIds,
    cells,
    cols,
    rows,
    innerSize.width,
    innerSize.height,
    x,
    y,
    props.reverse,
    props.gap,
    props.margin,
  ]);

  // console.log("Rendering shape element", {
  //   props,
  //   cpins,
  //   rpins,
  //   width,
  //   height,
  //   id,
  //   otherCellIds,
  //   otherCellCoords,
  //   cells,
  //   myIndex,
  //   myLevel,
  //   horizontal,
  //   alpha,
  //   gamma,
  //   shapeTotalHeight,
  //   shapeBaseWidth,
  //   sideLength,
  //   path,
  //   d,
  //   topHeight,
  //   dt,
  //   bottomHeight,
  //   db,
  //   ratio: dt / db,
  // });

  const strokeDashArray =
    props.outline === "dashed"
      ? props.outlineWeight
        ? props.outlineWeight * 3
        : 4
      : props.outline === "dotted"
        ? (props.outlineWeight ?? 1)
        : undefined;

  return (
    <div
      ref={shapeRef}
      style={{
        position: "absolute",
        zIndex: props.zIndex,
        width: "100%",
        height: "100%",
      }}
    >
      <svg
        className={`shape_${props.cellId}`}
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        style={{
          width: "100%",
          height: "100%",
          transform: props.reverse ? "rotate(180deg)" : undefined,
        }}
      >
        <defs>
          <style>
            {`
              #${id} {
                fill: ${props.fill};
                stroke: ${props.outline === "none" ? "unset" : (props.outlineColor ?? "black")};
                stroke-width: ${props.outlineWeight ?? 0}px;
                stroke-dasharray: ${strokeDashArray};
                shape-rendering: auto;
              }`}
          </style>
        </defs>

        <path id={id} d={path} />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: props.type === "Circle" ? "15%" : 0,
          right: props.type === "ArrowFull" || props.type === "Circle" ? "15%" : 0,
          overflow: props.type === "Circle" ? "clip" : undefined,
        }}
      >
        {props.children}
      </div>
    </div>
  );
};

CellShapeElement.propTypes = {
  children: PropTypes.element,
  type: PropTypes.string,
  outline: PropTypes.string,
  outlineWeight: PropTypes.number,
  outlineColor: PropTypes.string,
  fill: PropTypes.string,
  gap: PropTypes.number,
  margin: PropTypes.number,
  zIndex: PropTypes.number,
  reverse: PropTypes.bool,
  cellId: PropTypes.string,
  groupId: PropTypes.string,
  x: PropTypes.number,
  y: PropTypes.number,
};

export default React.memo(CellShapeElement);
